import { AsyncPipe, DecimalPipe, NgOptimizedImage, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
  input,
  outputBinding,
  inputBinding,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { Observable, Subscription, take } from 'rxjs';
import { InnerLineMap, LineMap, ProductLine, isProductLine, isProductLineMap } from 'src/app/models';
import { PickerType } from 'src/app/models/product-detail';
import { buttonIcons } from 'src/app/modules/icon.module';
import { BasketQuery } from 'src/app/services/basket/basket.query';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ContentService } from 'src/app/services/content/content.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { ProductService } from 'src/app/services/product';
import { findPackageCombination } from 'src/app/services/product/product-package-combination';
import { ProductTagService } from 'src/app/services/product/product-tag.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { SanitizeHtmlPipe } from 'src/app/shared/pipes';
import { environment } from 'src/environments/environment';
import {
  GetProductCategoryTags,
  Product,
  ProductComposition,
  ProductDetails,
  ProductPackageOption,
  Saving,
} from '../../../../models/product';
import { ContentImagePipe } from '../../../pipes/cms-image.pipe';
import { IconButtonComponent, TextButtonComponent } from '../../button';
import { CounterComponent } from '../counter/counter.component';
import { PriceFieldComponent } from '../price-field/price-field.component';
import { ProductDialogData } from './product-dialog/product-dialog.compontent';
import { SavingsComponent } from './savings/savings.component';

@Component({
  selector: 'app-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    NgTemplateOutlet,
    ContentImagePipe,
    SavingsComponent,
    NgStyle,
    MatIconModule,
    PriceFieldComponent,
    DecimalPipe,
    TextButtonComponent,
    AsyncPipe,
    CounterComponent,
    IconButtonComponent,
    NgOptimizedImage,
    SanitizeHtmlPipe,
  ],
})
export class ProductCardComponent extends ContentSection implements OnInit, AfterContentInit {
  private basketService = inject(BasketService);
  private basketQuery = inject(BasketQuery);
  private productService = inject(ProductService);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private dialogService = inject(DialogService);
  private productTagService = inject(ProductTagService);

  locale = environment.i18n.language;
  basketIcon = buttonIcons.basket;

  @Input() product!: Product;
  @Input() showBasketIcon: boolean;
  @Input() @HostBinding('class.big') big = false;
  @Input() @HostBinding('class.small') small = false;
  @Input() @HostBinding('class.hideImage') hideImage = false;
  @HostBinding('class.splash') splash = false;
  savings: Saving;
  price: number;

  // Used in product-picker.component
  @Input() baseCard: boolean;
  @Input() clearable: boolean;
  @Input() selectLabel: string;
  @Input() selectedLabel: string;
  @Input() locked = false;
  @Input() moduleType: PickerType;
  @Output() add = new EventEmitter<number>();
  @Output() remove = new EventEmitter<number>();

  readonly quantity = input<number>();
  readonly picked = input<boolean>();

  basketProducts$: Observable<LineMap>;
  lineId = 1;
  composition: ProductComposition;

  addToBasketLabel: string;
  chooseVariationLabel: string;
  advertisementLabel: string;
  originalPriceLabel: string;
  clearSelectionIcon = buttonIcons.bigCross;
  smallAltText = false;

  productUrl: string;
  summaryLabel: string;
  isDagrofa: boolean;
  primaryTag: GetProductCategoryTags;

  imageWidth: number;
  imageHeight: number;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'sharedContent');

    this.addToBasketLabel = this.content.get('addToBasketText');
    this.chooseVariationLabel = this.content.get('chooseVariationText') || 'Vælg variation';
    this.advertisementLabel = this.content.get('advertisementLabelText') || 'Se mere';
    this.originalPriceLabel = this.content.get('originalPriceLabelText');

    this.isDagrofa = this.contentService.isDagrofa;
  }

  ngOnInit() {
    this.basketProducts$ = this.basketQuery.selectProducts$;

    this.price = this.product.price;
    this.smallAltText = !this.containsNumbers(this.product.altPriceText);
    this.splash = this.product?.splash;

    this.summaryLabel = this.product.summary;
    if (this.isDagrofa && this.product.minCount > 0 && !this.moduleType) {
      this.summaryLabel = `Minimum ${this.product.minCount} kuverter`;
    }

    if (this.product.hasModules) {
      const composition = {
        ...this.product,
        moduleChoices: {},
        price: this.price,
        lineId: undefined,
      } as ProductComposition;
      this.composition = composition;
    }

    if (this.product.splash && this.product.originalPrice !== this.product.price) {
      const savingLabel = this.content.get('savingsLabelText');
      const savingPrice = this.product.originalPrice - this.product.price;

      this.savings = {
        quantity: 1,
        label: savingLabel,
        price: savingPrice,
      };
    }
    this.primaryTag = this.productTagService.getPrimaryTag(this.product.productCategoryTagId);

    switch (true) {
      case this.big:
        this.imageWidth = 266;
        this.imageHeight = 204;
        break;
      case this.isDagrofa:
        this.imageWidth = 300;
        this.imageHeight = 240;
        break;
      default:
        this.imageWidth = 192;
        this.imageHeight = 130;
    }

    this.productUrl = this.createProductUrl();
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  addToBasket(composite = false, lineId?: number) {
    let quantity = this.product.minCount;
    if (!this.product.packageOptions?.length) {
      if (composite) this.addCompositeProduct(quantity, lineId);
      else this.addProduct(quantity);
      return;
    }

    const packageOptions = [...this.product.packageOptions].sort((a, b) => b.packageSize - a.packageSize).map((o) => Object.assign({}, o));
    const packageSizes = packageOptions.map((o) => o.packageSize);
    let [sizeOne, sizeTwo] = findPackageCombination(quantity, packageSizes);

    if (sizeOne < 0) {
      quantity += sizeTwo;
      [sizeOne, sizeTwo] = findPackageCombination(quantity, packageSizes);
    }

    packageOptions[0].amount = sizeOne;
    if (packageOptions[1]) packageOptions[1].amount = sizeTwo;
    if (composite) this.addCompositeProduct(quantity, lineId, packageOptions);
    else this.addProduct(quantity, packageOptions);
  }

  addProduct(quantity: number, packageOptions?: ProductPackageOption[]) {
    quantity = quantity || 1;

    this.basketService.addProduct(this.product, quantity, packageOptions, true);
  }

  removeProduct(prevQuantity: number, quantity = 1, packageOptions?: ProductPackageOption[]) {
    this.composition = { ...this.composition };
    quantity = quantity || 1;

    if (prevQuantity === quantity) {
      this.basketService.deleteProduct(this.product);
      return;
    }

    this.basketService.removeProduct(this.product, quantity, packageOptions);
  }

  addCompositeProduct(quantity: number, lineId: number, packageOptions?: ProductPackageOption[]) {
    this.composition = { ...this.composition };
    quantity = quantity || 1;

    this.composition.lineId = lineId;
    this.basketService.addProduct(this.composition, quantity, packageOptions);
  }

  removeCompositeProduct(prevQuantity: number, quantity = 1, lineId: number, packageOptions?: ProductPackageOption[]) {
    quantity = quantity || 1;

    this.composition.lineId = lineId;
    if (prevQuantity === quantity) {
      this.basketService.deleteProduct(this.composition);
      this.composition.lineId = undefined;
      return;
    }

    this.basketService.removeProduct(this.composition, quantity, packageOptions);
  }

  createProductUrl(): string {
    return this.productService.createProductUrl(this.product);
  }

  isProductLine(line: ProductLine | InnerLineMap): ProductLine {
    if (!isProductLine(line)) {
      return undefined;
    }

    return line as ProductLine;
  }

  isInnerLineMap(line: any): ProductLine {
    if (!isProductLineMap(line)) {
      return undefined;
    }

    return line[this.lineId];
  }

  openProductDialog(e: Event) {
    e.preventDefault();
    const data: ProductDialogData = {
      product: this.product as ProductDetails,
      selectLabel: this.selectLabel,
      selectedLabel: this.selectedLabel,
      locked: this.locked,
      moduleType: this.moduleType,
    };
    this.dialogService.openProductDialog(data, [
      inputBinding('quantity', this.quantity),
      inputBinding('picked', this.picked),
      outputBinding<number>('add', (event) => this.add.emit(event)),
      outputBinding<number>('remove', (event) => this.remove.emit(event)),
    ]);
  }

  private containsNumbers(str: string): boolean {
    if (!str) {
      return false;
    }

    return /[0-9]/i.test(str);
  }
}
